import { useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon, type IconName } from '../shared/Icons';
import { Logo } from '../shared/Logo';
import { Avatar } from '../shared/Avatar';
import { Button } from '../shared/Button';
import { SponsorSlot } from '../shared/SponsorSlot';
import { SponsorRail } from '../shared/SponsorRail';
import { SIDEBAR_SPONSORS } from '../../lib/partners';
import { useAuth } from '../../contexts/auth-context';
import { countryFlag } from '../../lib/country-flags';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Referee navigation (from the prototype). Only Home is real this slice; the rest land on a
// "coming soon" placeholder so the shell is fully navigable.
const NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/clips', label: 'Video clips', icon: 'clip' },
  { to: '/rule-tests', label: 'Rule tests', icon: 'check' },
  { to: '/exams', label: 'Video clip exams', icon: 'flask' },
  { to: '/bookmarked', label: 'Bookmarked', icon: 'bookmark' },
  { to: '/documents', label: 'Documents', icon: 'doc' },
  { to: '/webinars', label: 'Webinars', icon: 'users' },
  { to: '/leaderboard', label: 'Leaderboard', icon: 'trophy' },
];

// super_admin is a global ProRef operator, not a referee — it gets its own console (manage
// the content/tenant catalogue + the clip-management module) and never sees the referee
// daily-loop nav.
const SUPER_ADMIN_NAV: NavItem[] = [
  { to: '/federations', label: 'Federations', icon: 'building' },
  { to: '/users', label: 'Users', icon: 'users' },
  { to: '/sports', label: 'Sports', icon: 'whistle' },
  { to: '/rulebooks', label: 'Rulebooks', icon: 'doc' },
  // The Leaderboard's Period="Season" calendar — platform-wide, not tied to any one federation.
  { to: '/seasons', label: 'Seasons', icon: 'calendar' },
  // super_admin authors PLATFORM documents (sport-wide rule-books/signal-codes every federation
  // sees); the referee/fed_admin nav already links Documents, so this is the only role that would
  // otherwise have no route to the upload dialog.
  { to: '/documents', label: 'Documents', icon: 'doc' },
  { to: '/admin/clips', label: 'Clips', icon: 'clip' },
  { to: '/admin/daily-queue', label: 'Daily Queue', icon: 'calendar' },
  { to: '/admin/exams', label: 'Exams', icon: 'flask' },
  // Cost board — super_admin only, deliberately absent from FEDERATION_ADMIN_NAV below.
  { to: '/admin/costs', label: 'Costs', icon: 'chart' },
];
// federation_admin operates inside its own federation: referee nav (where "Video clips"
// → /clips is the browse view) PLUS this admin group — Invites + the clip-management
// module at /admin/clips. The admin Clips entry intentionally lives in this group (not
// the referee NAV) so the IA stays clean: browse vs manage are different surfaces.
const FEDERATION_ADMIN_NAV: NavItem[] = [
  { to: '/admin/analytics', label: 'Analytics', icon: 'chart' },
  { to: '/invites', label: 'Users', icon: 'users' },
  { to: '/admin/clips', label: 'Clips', icon: 'clip' },
  { to: '/admin/daily-queue', label: 'Daily Queue', icon: 'calendar' },
  { to: '/admin/exams', label: 'Exams', icon: 'flask' },
];

// Nav is role-shaped, not a shared list with bolt-on admin items: a super_admin sees only the
// console; a federation_admin gets the referee nav plus its admin group; a referee gets just the nav.
function navGroups(role: string | null): NavGroup[] {
  if (role === 'super_admin') return [{ label: 'Console', items: SUPER_ADMIN_NAV }];
  const groups: NavGroup[] = [{ label: 'Navigate', items: NAV }];
  if (role === 'federation_admin') groups.push({ label: 'Admin', items: FEDERATION_ADMIN_NAV });
  return groups;
}

const ROLE_LABEL: Record<string, string> = {
  referee: 'Referee',
  federation_admin: 'Federation admin',
  super_admin: 'ProRef admin',
};

function SidebarItem({ item, compact }: { item: NavItem; compact: boolean }) {
  const Glyph = Icon[item.icon];
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={compact ? item.label : undefined}
      // The active surface/border/shadow now lives on the sliding pill (see Sidebar) so it can
      // animate between rows — the item itself only owns its text/icon colour, eased so the swap
      // reads smoothly alongside the pill. `relative z-[1]` keeps the label above the pill.
      className={({ isActive }) =>
        [
          'relative z-[1] flex h-10 items-center gap-2.5 rounded-sm border border-transparent px-2.5 text-[13.5px] font-medium transition-colors',
          compact ? 'justify-center' : '',
          isActive ? 'text-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Glyph className={'w-4 h-4 flex-none transition-colors ' + (isActive ? 'text-accent' : 'text-ink-3')} />
          {!compact && <span className="min-w-0 truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

interface SidebarProps {
  compact: boolean;
  onToggleCompact: () => void;
  // Positioning is owned by AppShell, which renders this as a permanent grid column at lg and up
  // and as an off-canvas drawer below it — the sidebar itself only knows how to lay out its rows.
  className?: string;
  // Collapse is a desktop-only affordance (a drawer is already dismissible), so the shell hides
  // the toggle below lg rather than this component guessing at the viewport.
  showCompactToggle?: boolean;
  // Dismiss the drawer — rendered as an X beside the brand, below lg only.
  onClose?: () => void;
}

export function Sidebar({
  compact,
  onToggleCompact,
  className = '',
  showCompactToggle = true,
  onClose,
}: SidebarProps) {
  const { user, role } = useAuth();
  const name = user?.full_name ?? 'ProRef user';
  // Footer profile subline: nation flag + role + federation acronym, e.g. "🇭🇷 Referee • HRS". The
  // flag/acronym only exist for federation members (null for super_admin), so each part is optional.
  const flag = countryFlag(user?.federation_country);
  const roleLabel = role ? ROLE_LABEL[role] : '';
  const profileSub = [roleLabel, user?.federation_acronym].filter(Boolean).join(' • ');
  // Whole nav is derived from the role — super_admin gets the console, others the referee nav.
  const groups = navGroups(role);
  // The sponsor rail (referee-only, expanded only) — a federation_admin is operating the tenant,
  // not training in it, so an ad rail in its console reads as clutter; same reasoning that already
  // excluded super_admin. When shown it takes the leftover vertical space, so the nav drops to
  // content height; when hidden the nav keeps flex-1 to pin the footer.
  const showSponsor = !compact && role === 'referee';

  // Sliding active-highlight: one pill element that moves to overlay whichever nav item is active,
  // so navigating slides the highlight to the new row instead of it vanishing and reappearing.
  const navRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const [pill, setPill] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  // Hidden (faded out) whenever the current route matches no nav item, so the pill doesn't linger
  // on a stale row — but we keep its last coords so it fades in place instead of jumping.
  const [pillVisible, setPillVisible] = useState(false);

  // useLayoutEffect (not useEffect) so the pill is positioned before the browser paints — the first
  // placement lands with no slide; only later route/size changes animate.
  useLayoutEffect(() => {
    const container = navRef.current;
    if (!container) return;
    const measure = () => {
      // React Router marks the active NavLink with aria-current="page" — the reliable hook for
      // "which row is active" across both nav groups.
      const active = container.querySelector('[aria-current="page"]') as HTMLElement | null;
      if (!active) {
        setPillVisible(false);
        return;
      }
      const c = container.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      // Coordinates relative to the scrollable content (add scroll offset) so the pill, which lives
      // inside the scroll container, tracks the row as the nav scrolls.
      setPill({
        top: a.top - c.top + container.scrollTop,
        left: a.left - c.left + container.scrollLeft,
        width: a.width,
        height: a.height,
      });
      setPillVisible(true);
    };
    measure();
    // Re-measure when the sidebar resizes — collapse/expand changes every row's width, a window
    // resize can reflow, and role/nav changes swap the rows out. jsdom (tests) has no
    // ResizeObserver; the pill still positions on route change there, it just won't track resizes.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [pathname, compact, role]);

  return (
    <aside className={'flex h-screen flex-col overflow-hidden border-r border-line bg-bg-2 ' + className}>
      {/* Brand + collapse toggle */}
      <div className={'flex items-center gap-2 px-4 pb-2.5 pt-[18px] ' + (compact ? 'justify-center' : 'justify-between')}>
        {!compact && (
          <div className="flex items-center gap-2">
            <Logo size={18} />
            {/* Make the super_admin console unmistakable — a small system-console badge by the wordmark. */}
            {role === 'super_admin' && (
              <span className="rounded-full bg-accent px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase leading-none tracking-[0.08em] text-accent-ink">
                Super&nbsp;Admin
              </span>
            )}
          </div>
        )}
        {showCompactToggle && (
          <Button
            variant="ghost"
            icon
            className="h-9 w-9"
            onClick={onToggleCompact}
            title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon.menu className="w-4 h-4" />
          </Button>
        )}
        {/* Drawer dismiss. `lg:hidden` as well as the onClose guard: at lg the sidebar is a
            permanent column with nothing to close. */}
        {onClose && (
          <Button
            variant="ghost"
            icon
            className="h-9 w-9 lg:hidden"
            onClick={onClose}
            title="Close navigation"
            aria-label="Close navigation"
          >
            <Icon.close className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Federation context — which tenant the federation_admin/referee is operating in. Absent for
          super_admin (global, no federation; it carries the SUPER ADMIN badge instead). */}
      {!compact && user?.federation_name && (
        <div className="mx-3 mb-1 mt-0.5 rounded-sm border border-line bg-surface px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Icon.building className="w-3.5 h-3.5 flex-none text-accent" />
            <span className="min-w-0 truncate text-[12.5px] font-semibold text-ink" title={user.federation_name}>
              {user.federation_name}
            </span>
          </div>
          <div className="mt-0.5 truncate pl-[22px] text-[11px] text-muted">
            {role ? ROLE_LABEL[role] : ''}
            {user.sport_name ? ` · ${user.sport_name}` : ''}
          </div>
        </div>
      )}

      {/* Nav */}
      <div
        ref={navRef}
        className={
          'scroll-thin relative min-h-0 overflow-y-auto px-2.5 pb-3 pt-1.5 ' + (showSponsor ? '' : 'flex-1')
        }
      >
        {/* The sliding highlight. Sits behind the rows (they're z-[1]); transitions its transform +
            size so it glides to the active row. Only rendered once measured, so it never slides in
            from the origin on first paint. */}
        {pill && (
          <div
            aria-hidden="true"
            className={
              'pointer-events-none absolute left-0 top-0 rounded-sm border border-line bg-surface shadow-1 transition-all duration-300 ease-out ' +
              (pillVisible ? 'opacity-100' : 'opacity-0')
            }
            style={{
              transform: `translate(${pill.left}px, ${pill.top}px)`,
              width: pill.width,
              height: pill.height,
            }}
          >
            {/* The accent bar in the left gutter (was the item's before-pseudo), now on the pill. */}
            <span className="absolute -left-2.5 top-2 bottom-2 w-0.5 rounded-full bg-accent" />
          </div>
        )}
        {groups.map((group, i) => (
          <div key={group.label}>
            {!compact ? (
              <div className="px-2.5 pb-1.5 pt-3.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-2">
                {group.label}
              </div>
            ) : (
              // Keep a little breathing room between groups when collapsed (no header to space them).
              i > 0 && <div className="h-3" />
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <SidebarItem key={item.to} item={item} compact={compact} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Sponsor rail — a partner/ad placeholder that grows to fill the space between the nav and the
          profile. Referees only: neither admin role (federation or ProRef) carries it; hidden when
          the rail is collapsed. */}
      {showSponsor &&
        (SIDEBAR_SPONSORS.length > 0 ? (
          <SponsorRail sponsors={SIDEBAR_SPONSORS} className="mx-3 mb-3 mt-1 min-h-0 flex-1" />
        ) : (
          <SponsorSlot variant="rail" className="mx-3 mb-3 mt-1 min-h-0 flex-1" />
        ))}

      {/* Footer profile */}
      <div className="border-t border-line bg-bg-2 px-3.5 py-3">
        <div className={'flex items-center gap-2.5 ' + (compact ? 'justify-center' : '')}>
          <Avatar name={name} size="md" />
          {!compact && (
            <div className="min-w-0">
              <div className="truncate text-[12.5px] font-semibold text-ink">{name}</div>
              <div className="flex items-center gap-1 text-[11px] text-muted">
                {flag && (
                  // Emoji glyphs carry taller intrinsic metrics than 11px text; matching leading-none
                  // on both spans collapses each to a tight box so items-center lands them on one line.
                  <span aria-hidden="true" className="shrink-0 text-[12px] leading-none">
                    {flag}
                  </span>
                )}
                <span className="truncate leading-none">{profileSub}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
